<template>
	<Card
		:loading
		:embedded
		:bordered
		class="rounded-lg transition-all duration-300"
		:size
		:content-class="`flex items-center gap-4 ${wrapSlots ? 'flex-wrap' : ''} ${cardClass || ''} ${vertical ? 'flex-col items-start' : ''}`"
		:class="{
			'inset-ring-primary cursor-pointer hover:inset-ring': clickable,
			'inset-ring-primary bg-primary-100! inset-ring': selected,
			'bg-primary-700!': theme === 'dark',
			'relative overflow-hidden': theme === 'dark' || showShape
		}"
	>
		<img
			v-if="showShape"
			:src="cardShape"
			alt=""
			class="pointer-events-none absolute top-0 right-0 z-0"
			width="199"
			height="252"
		/>

		<template v-if="skeleton || placeholder">
			<div class="relative z-1 flex grow items-center gap-3.5">
				<n-skeleton
					circle
					height="40px"
					width="40px"
					:animated="!!skeleton"
					style="--n-color-start: var(--grey-300-color)"
				/>
				<div class="flex grow flex-col gap-2">
					<n-skeleton round height="16px" width="80%" :animated="!!skeleton" />
					<n-skeleton round height="16px" width="60%" :animated="!!skeleton" />
				</div>
			</div>
			<div class="relative z-1 flex items-center justify-end">
				<n-skeleton height="40px" :sharp="false" width="40px" :animated="!!skeleton" />
			</div>
		</template>
		<template v-else>
			<div
				class="relative z-1 flex w-full items-center gap-4"
				:class="[vertical ? 'flex-col items-start' : '', contentClass]"
			>
				<div
					v-if="icon || $slots.icon"
					:class="{
						'bg-primary-200 flex size-11 min-h-11 min-w-11 items-center justify-center rounded-lg':
							!hideIconWrapper,
						'rounded-full!': iconShape === 'circle' && !hideIconWrapper
					}"
				>
					<Icon v-if="icon && !$slots.icon" :name="icon" class="text-primary" :size="22" />
					<slot name="icon" />
				</div>

				<slot name="image" />

				<div
					v-if="title || $slots.title || description || $slots.description"
					class="flex flex-col gap-1"
					:class="headerClass"
				>
					<div
						v-if="title || $slots.title"
						class="leading-tight font-medium"
						:class="{ 'text-white': theme === 'dark' }"
					>
						<template v-if="title && !$slots.title">{{ title }}</template>
						<slot name="title" />
					</div>
					<div
						v-if="description || $slots.description"
						class="text-sm leading-tight"
						:class="theme === 'dark' ? 'text-grey-300' : 'text-secondary'"
					>
						<template v-if="description && !$slots.description">{{ description }}</template>
						<slot name="description" />
					</div>
				</div>
			</div>
			<div
				v-if="$slots.extra && !hideExtraSlot"
				class="relative z-1 flex items-stretch gap-4"
				:class="extraClass"
			>
				<n-divider
					v-if="segmented"
					:vertical="!vertical"
					:class="{ 'h-auto! min-h-full': !vertical, 'my-0!': vertical }"
				/>
				<slot name="extra" />
			</div>
		</template>
	</Card>
</template>

<script setup lang="ts">
import type { CardSize } from "naive-ui"
import { NDivider, NSkeleton } from "naive-ui"
import cardShape from "@/assets/images/card-shape.svg?url"
import Card from "@/components/common/Card.vue"
import Icon from "@/components/common/Icon.vue"

const {
	icon,
	title,
	description,
	segmented,
	clickable,
	skeleton,
	placeholder,
	bordered = true,
	embedded,
	loading,
	wrapSlots,
	iconShape,
	cardClass,
	contentClass,
	headerClass,
	extraClass,
	size,
	vertical,
	selected,
	hideIconWrapper,
	hideExtraSlot,
	theme,
	showShape
} = defineProps<{
	size?: CardSize
	icon?: string
	title?: string
	description?: string
	segmented?: boolean
	clickable?: boolean
	skeleton?: boolean
	placeholder?: boolean
	bordered?: boolean
	embedded?: boolean
	loading?: boolean
	wrapSlots?: boolean
	iconShape?: "circle"
	cardClass?: string
	contentClass?: string
	headerClass?: string
	extraClass?: string
	vertical?: boolean
	selected?: boolean
	hideIconWrapper?: boolean
	hideExtraSlot?: boolean
	theme?: "dark"
	showShape?: boolean
}>()
</script>
